import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import DateUtility from "./dateUtility";
/**
 * "Vælg uge" of the Vagtplan (boards VagtplanUge and VagtplanBredUge): a button that says which
 * week is on screen and opens half a year of weeks, one row per month. A phone gets it as a sheet
 * from the bottom, a wide screen as a pane under the button.
 */
const ICON_PROPS = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
};
const halfOf = (dt) => ({ year: dt.year, half: dt.month <= 6 ? 1 : 2 });
/** The Mondays of the weeks that touch the month: a week on the turn of two months is in both rows. */
const weeksOfMonth = (year, month) => {
    const first = DateTime.local(year, month, 1);
    const last = first.endOf("month");
    const weeks = [];
    for (let monday = first.startOf("week"); monday <= last; monday = monday.plus({ weeks: 1 })) {
        weeks.push(monday);
    }
    return weeks;
};
export default function WeekPicker({ week, wide, onSelect }) {
    const [open, setOpen] = useState(false);
    const [half, setHalf] = useState(halfOf(week));
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const thisWeek = DateTime.now().startOf("week");
    const selected = week.startOf("week");
    const close = () => {
        var _a;
        setOpen(false);
        (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                close();
            }
        };
        const onPointer = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onPointer);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onPointer);
        };
    }, [open]);
    const toggle = () => {
        if (!open) {
            setHalf(halfOf(selected));
        }
        setOpen(!open);
    };
    const step = (dir) => {
        const index = half.year * 2 + (half.half - 1) + dir;
        setHalf({ year: Math.floor(index / 2), half: ((index % 2) + 1) });
    };
    const pick = (monday) => {
        setOpen(false);
        onSelect(monday);
    };
    const months = half.half === 1 ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
    const weekClass = (monday) => {
        if (monday.equals(selected))
            return "bi-wp-week bi-wp-week-selected";
        if (monday.equals(thisWeek))
            return "bi-wp-week bi-wp-week-current";
        if (monday < thisWeek)
            return "bi-wp-week bi-wp-week-past";
        return "bi-wp-week";
    };
    const weekLabel = (monday) => {
        const n = monday.weekNumber;
        if (monday.equals(selected))
            return `Uge ${n}, valgt`;
        if (monday.equals(thisWeek))
            return `Uge ${n}, denne uge`;
        return `Uge ${n}`;
    };
    return (React.createElement("span", { className: "bi-wp", ref: rootRef },
        React.createElement("button", { type: "button", ref: buttonRef, className: "bi-wp-button", "aria-haspopup": "dialog", "aria-expanded": open, "aria-label": `Vælg uge, uge ${selected.weekNumber} ${selected.weekYear}`, onClick: toggle },
            React.createElement("svg", Object.assign({}, ICON_PROPS, { className: "bi-wp-icon" }),
                React.createElement("rect", { x: "3", y: "5", width: "18", height: "16", rx: "2" }),
                React.createElement("path", { d: "M3 10h18M8 3v4M16 3v4" })),
            wide ? `Uge ${selected.weekNumber} · ${selected.weekYear}` : `Uge ${selected.weekNumber}`,
            React.createElement("svg", Object.assign({}, ICON_PROPS, { className: "bi-wp-chevron" }),
                React.createElement("path", { d: "m6 9 6 6 6-6" }))),
        open ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "bi-wp-scrim", onClick: close }),
            React.createElement("div", { className: "bi-wp-pane", role: "dialog", "aria-label": "V\u00E6lg uge" },
                React.createElement("div", { className: "bi-wp-grip", "aria-hidden": "true" }),
                React.createElement("div", { className: "bi-wp-half" },
                    React.createElement("button", { type: "button", className: "bi-wp-step", "aria-label": "Forrige halv\u00E5r", onClick: () => step(-1) },
                        React.createElement("svg", Object.assign({}, ICON_PROPS),
                            React.createElement("path", { d: "m15 6-6 6 6 6" }))),
                    React.createElement("span", { className: "bi-wp-half-title" },
                        half.half,
                        ". halv\u00E5r ",
                        half.year),
                    React.createElement("button", { type: "button", className: "bi-wp-step", "aria-label": "N\u00E6ste halv\u00E5r", onClick: () => step(1) },
                        React.createElement("svg", Object.assign({}, ICON_PROPS),
                            React.createElement("path", { d: "m9 6 6 6-6 6" })))),
                React.createElement("div", { className: "bi-wp-jump" },
                    React.createElement("button", { type: "button", onClick: () => pick(thisWeek) }, "G\u00E5 til i dag"),
                    React.createElement("button", { type: "button", onClick: () => setHalf(halfOf(selected)) }, "G\u00E5 til valgt uge")),
                React.createElement("div", { className: "bi-wp-months" }, months.map((month) => (React.createElement("div", { className: "bi-wp-month", key: month },
                    React.createElement("span", { className: "bi-wp-month-name" }, DateUtility.monthShort(DateTime.local(half.year, month, 1)).toLowerCase()),
                    weeksOfMonth(half.year, month).map((monday) => (React.createElement("button", { type: "button", key: monday.toISODate(), className: weekClass(monday), "aria-label": weekLabel(monday), "aria-pressed": monday.equals(selected), onClick: () => pick(monday) }, monday.weekNumber))))))),
                React.createElement("div", { className: "bi-wp-legend" },
                    React.createElement("span", null,
                        React.createElement("i", { className: "bi-wp-key bi-wp-key-selected" }),
                        "Valgt uge"),
                    React.createElement("span", null,
                        React.createElement("i", { className: "bi-wp-key bi-wp-key-current" }),
                        "Denne uge"),
                    React.createElement("span", { className: "bi-wp-legend-note" }, "Gr\u00E5 tal: tidligere uger"))))) : null));
}
